/**
 * How live alarms reach THIS PERSON ON THIS DEVICE: which ones pop up as toasts, and whether a
 * critical one makes a sound.
 *
 * ON THE DEVICE, NOT ON THE SERVER, and that is the honest scope rather than a shortcut: both
 * are about the screen in front of the reader — a control-room wall wants sound, a laptop in a
 * meeting does not — exactly like light/dark (`theme.ts`). Defaults reproduce today's behaviour
 * (every toast, no sound), so nobody's console changes until they choose.
 *
 * WHAT THIS DOES NOT TOUCH: the bell's list and the Alarms board. A muted toast is still an
 * alarm — it is raised, counted, listed and escalated exactly as before. Escalation email is the
 * organisation's alarm policy, not a personal preference, and is not offered here.
 */
import { create } from 'zustand';
import { PRODUCT } from '@/product/profile';

export type ToastLevel = 'all' | 'critical' | 'off';

/** The design's unread indicator: a red dot, or the number itself. */
export type BadgeStyle = 'dot' | 'count';

/**
 * This device's share of the Notification screen.
 *
 * `toasts` and `sound` are this file's originals. `content`, `bell` and `badge` are the FESCO
 * design's *General Notifications* group (`Notification.png`), which lives here rather than on
 * the server because the design's own blurb says it is about "your device" — and because every
 * one of them is a question about the screen in front of the reader. The other four groups are
 * per-person and are the server's (`Lumen-BE account/notifications.ts`).
 *
 * Defaults reproduce today's behaviour exactly, so nobody's console changed when these arrived.
 */
export interface NotifyPrefs {
  toasts: ToastLevel;
  sound: boolean;
  /** Design: "Show content of notification". Off: a toast says what happened, not where. */
  content: boolean;
  /** Design: "Show bell icon on System Bar". Off: the header has no bell. */
  bell: boolean;
  /** Design: "Red Dot for unread" / "Count for unread". */
  badge: BadgeStyle;
}

export const DEFAULT_PREFS: NotifyPrefs = { toasts: 'all', sound: false, content: true, bell: true, badge: 'count' };

const KEY = `${PRODUCT.storagePrefix}.notify`;

/** Pure, for the test: anything unreadable falls back field by field to the defaults. */
export function parsePrefs(raw: string | null): NotifyPrefs {
  try {
    const v = raw === null ? {} : (JSON.parse(raw) as Partial<NotifyPrefs>);
    return {
      toasts: v.toasts === 'critical' || v.toasts === 'off' || v.toasts === 'all' ? v.toasts : DEFAULT_PREFS.toasts,
      sound: typeof v.sound === 'boolean' ? v.sound : DEFAULT_PREFS.sound,
      content: typeof v.content === 'boolean' ? v.content : DEFAULT_PREFS.content,
      bell: typeof v.bell === 'boolean' ? v.bell : DEFAULT_PREFS.bell,
      badge: v.badge === 'dot' || v.badge === 'count' ? v.badge : DEFAULT_PREFS.badge,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export const isCritical = (severity: string): boolean => severity.toLowerCase().includes('critical');

/**
 * Whether a live event pops up. A reminder always does — it is addressed to this person by name
 * ("waiting on you", "you are the shift lead"), and muting it would defeat the escalation it is.
 */
export function shouldToast(a: { event: string; severity: string }, level: ToastLevel): boolean {
  if (a.event === 'reminder') return true;
  if (level === 'off') return false;
  if (level === 'critical') return isCritical(a.severity) && a.event !== 'closed';
  return true;
}

/**
 * Whether an event makes the critical sound: a critical alarm raised or raised again.
 *
 * Takes only the field it reads. It used to take the whole `NotifyPrefs`, which meant every
 * caller and every test had to make up values for switches this function does not look at.
 */
export function shouldChime(a: { event: string; severity: string }, prefs: Pick<NotifyPrefs, 'sound'>): boolean {
  return prefs.sound && (a.event === 'opened' || a.event === 'reopened') && isCritical(a.severity);
}

function read(): NotifyPrefs {
  try {
    return parsePrefs(localStorage.getItem(KEY));
  } catch {
    return DEFAULT_PREFS;
  }
}

interface NotifyState extends NotifyPrefs {
  set: (patch: Partial<NotifyPrefs>) => void;
}

export const useNotifyPrefs = create<NotifyState>((set, get) => ({
  ...read(),
  set: (patch) => {
    const next: NotifyPrefs = { toasts: get().toasts, sound: get().sound, content: get().content, bell: get().bell, badge: get().badge, ...patch };
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* private window — the choice holds for this visit */
    }
    set(next);
  },
}));

/**
 * A short two-note tone, synthesised rather than shipped as a file: no asset in the bundle, and
 * nothing to fetch at the moment an alarm needs attention. Browsers only allow audio after the
 * page has had a gesture, which a signed-in console always has; if it has not, this is silent.
 */
export function chime(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.18 + 0.16);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.18);
      osc.stop(now + i * 0.18 + 0.17);
    });
    window.setTimeout(() => void ctx.close(), 600);
  } catch {
    /* audio blocked or unavailable — the toast and the bell still carry the alarm */
  }
}
